import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Plus,
    Pencil,
    Trash2,
    BookOpen,
    Lock,
    Unlock,
    ChevronRight,
    Upload,
    Video,
    X,
    CheckCircle,
    Loader2,
    Youtube,
    Link as LinkIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLevelsPage = () => {
    const { language } = useLanguage();
    const [levels, setLevels] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [levelDialog, setLevelDialog] = useState(false);
    const [chapterDialog, setChapterDialog] = useState(false);
    const [lessonDialog, setLessonDialog] = useState(false);
    
    const [editingLevel, setEditingLevel] = useState(null);
    const [editingChapter, setEditingChapter] = useState(null);
    const [editingLesson, setEditingLesson] = useState(null);
    
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [selectedChapter, setSelectedChapter] = useState(null);

    // Ensure auth token is set on component mount
    useEffect(() => {
        const token = localStorage.getItem('nusatrade_token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, []);

    // Form states
    const [levelForm, setLevelForm] = useState({ name: '', name_en: '', description: '', description_en: '', order: 0, icon: 'book' });
    const [chapterForm, setChapterForm] = useState({ title: '', title_en: '', description: '', description_en: '', order: 0, level_id: '' });
    const [lessonForm, setLessonForm] = useState({ title: '', title_en: '', description: '', description_en: '', video_url: '', duration_minutes: 10, order: 0, chapter_id: '', is_locked: true });
    
    // Video upload states
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [videoSourceTab, setVideoSourceTab] = useState('youtube');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const videoInputRef = useRef(null);

    // Helper function to extract YouTube video ID and convert to embed URL
    const getYoutubeEmbedUrl = (url) => {
        if (!url) return null;
        
        // Handle various YouTube URL formats including Shorts
        const patterns = [
            /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,           // youtube.com/watch?v=
            /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,                        // youtu.be/
            /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,              // youtube.com/embed/
            /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,             // youtube.com/shorts/
            /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,                  // youtube.com/v/
            /^([a-zA-Z0-9_-]{11})$/                                       // Just the video ID
        ];
        
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return `https://www.youtube.com/embed/${match[1]}`;
            }
        }
        return null;
    };

    // Check if URL is a YouTube embed URL
    const isYoutubeUrl = (url) => {
        if (!url) return false;
        return url.includes('youtube.com/embed/') || 
               url.includes('youtube.com/watch') || 
               url.includes('youtube.com/shorts/') ||
               url.includes('youtu.be/');
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [levelsRes, chaptersRes, lessonsRes] = await Promise.all([
                axios.get(`${API}/levels`),
                axios.get(`${API}/chapters`),
                axios.get(`${API}/lessons`)
            ]);
            setLevels(levelsRes.data);
            setChapters(chaptersRes.data);
            setLessons(lessonsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Level CRUD
    const handleSaveLevel = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            if (editingLevel) {
                await axios.put(`${API}/admin/levels/${editingLevel.id}`, levelForm);
                toast.success(language === 'id' ? 'Level diupdate!' : 'Level updated!');
            } else {
                await axios.post(`${API}/admin/levels`, levelForm);
                toast.success(language === 'id' ? 'Level ditambahkan!' : 'Level added!');
            }
            setLevelDialog(false);
            setEditingLevel(null);
            setLevelForm({ name: '', name_en: '', description: '', description_en: '', order: 0, icon: 'book' });
            fetchData();
        } catch (error) {
            toast.error('Error saving level');
        }
    };

    const handleDeleteLevel = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus level ini?' : 'Are you sure to delete this level?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            await axios.delete(`${API}/admin/levels/${id}`);
            toast.success(language === 'id' ? 'Level dihapus!' : 'Level deleted!');
            fetchData();
        } catch (error) {
            toast.error('Error deleting level');
        }
    };

    // Chapter CRUD
    const handleSaveChapter = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (!token) {
                toast.error(language === 'id' ? 'Sila login semula' : 'Please login again');
                return;
            }
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            
            if (editingChapter) {
                await axios.put(`${API}/admin/chapters/${editingChapter.id}`, chapterForm, config);
                toast.success(language === 'id' ? 'Chapter diupdate!' : 'Chapter updated!');
            } else {
                await axios.post(`${API}/admin/chapters`, chapterForm, config);
                toast.success(language === 'id' ? 'Chapter ditambahkan!' : 'Chapter added!');
            }
            setChapterDialog(false);
            setEditingChapter(null);
            setChapterForm({ title: '', title_en: '', description: '', description_en: '', order: 0, level_id: '' });
            fetchData();
        } catch (error) {
            console.error('Save chapter error:', error);
            toast.error(language === 'id' ? 'Gagal simpan chapter' : 'Error saving chapter');
        }
    };

    const handleDeleteChapter = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus chapter ini?' : 'Are you sure to delete this chapter?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            await axios.delete(`${API}/admin/chapters/${id}`, config);
            toast.success(language === 'id' ? 'Chapter dihapus!' : 'Chapter deleted!');
            fetchData();
        } catch (error) {
            toast.error('Error deleting chapter');
        }
    };

    // Lesson CRUD
    const handleSaveLesson = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (!token) {
                toast.error(language === 'id' ? 'Sila login semula' : 'Please login again');
                return;
            }
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            
            if (editingLesson) {
                await axios.put(`${API}/admin/lessons/${editingLesson.id}`, lessonForm, config);
                toast.success(language === 'id' ? 'Lesson diupdate!' : 'Lesson updated!');
            } else {
                await axios.post(`${API}/admin/lessons`, lessonForm, config);
                toast.success(language === 'id' ? 'Lesson ditambahkan!' : 'Lesson added!');
            }
            setLessonDialog(false);
            setEditingLesson(null);
            setLessonForm({ title: '', title_en: '', description: '', description_en: '', video_url: '', duration_minutes: 10, order: 0, chapter_id: '', is_locked: true });
            setYoutubeUrl('');
            fetchData();
        } catch (error) {
            console.error('Save lesson error:', error);
            toast.error(language === 'id' ? 'Gagal simpan lesson' : 'Error saving lesson');
        }
    };

    const handleDeleteLesson = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus lesson ini?' : 'Are you sure to delete this lesson?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            await axios.delete(`${API}/admin/lessons/${id}`, config);
            toast.success(language === 'id' ? 'Lesson dihapus!' : 'Lesson deleted!');
            fetchData();
        } catch (error) {
            toast.error('Error deleting lesson');
        }
    };

    const toggleLessonLock = async (lesson) => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            await axios.put(`${API}/admin/lessons/${lesson.id}/lock?is_locked=${!lesson.is_locked}`, {}, config);
            toast.success(lesson.is_locked 
                ? (language === 'id' ? 'Lesson dibuka!' : 'Lesson unlocked!')
                : (language === 'id' ? 'Lesson dikunci!' : 'Lesson locked!')
            );
            fetchData();
        } catch (error) {
            toast.error('Error toggling lock');
        }
    };

    // Video upload handler
    const handleVideoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
        if (!allowedTypes.includes(file.type)) {
            toast.error(language === 'id' 
                ? 'Format tidak disokong. Gunakan MP4, WebM, OGG, MOV, atau AVI'
                : 'Unsupported format. Use MP4, WebM, OGG, MOV, or AVI'
            );
            return;
        }

        // Validate file size (500MB max)
        if (file.size > 500 * 1024 * 1024) {
            toast.error(language === 'id' 
                ? 'Fail terlalu besar. Maksimum 500MB'
                : 'File too large. Maximum 500MB'
            );
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('nusatrade_token');
            const response = await axios.post(`${API}/admin/upload/video`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                },
            });

            if (response.data.success) {
                setLessonForm({ ...lessonForm, video_url: response.data.video_url });
                toast.success(language === 'id' ? 'Video berjaya dimuat naik!' : 'Video uploaded successfully!');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || (language === 'id' ? 'Gagal muat naik video' : 'Failed to upload video'));
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (videoInputRef.current) {
                videoInputRef.current.value = '';
            }
        }
    };

    const clearVideoUrl = () => {
        setLessonForm({ ...lessonForm, video_url: '' });
        setYoutubeUrl('');
    };

    const handleYoutubeUrl = () => {
        const embedUrl = getYoutubeEmbedUrl(youtubeUrl);
        if (embedUrl) {
            setLessonForm({ ...lessonForm, video_url: embedUrl });
            toast.success(language === 'id' ? 'YouTube URL ditambahkan!' : 'YouTube URL added!');
        } else {
            toast.error(language === 'id' ? 'URL YouTube tidak valid' : 'Invalid YouTube URL');
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-white">
                            {language === 'id' ? 'Kelola Level & Lesson' : 'Manage Levels & Lessons'}
                        </h1>
                        <p className="text-slate-400 mt-1">
                            {levels.length} levels, {chapters.length} chapters, {lessons.length} lessons
                        </p>
                    </div>
                    <Dialog open={levelDialog} onOpenChange={setLevelDialog}>
                        <DialogTrigger asChild>
                            <Button 
                                onClick={() => {
                                    setEditingLevel(null);
                                    setLevelForm({ name: '', name_en: '', description: '', description_en: '', order: levels.length + 1, icon: 'book' });
                                }}
                                data-testid="add-level-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'id' ? 'Tambah Level' : 'Add Level'}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-800 border-slate-700 text-white">
                            <DialogHeader>
                                <DialogTitle>
                                    {editingLevel 
                                        ? (language === 'id' ? 'Edit Level' : 'Edit Level')
                                        : (language === 'id' ? 'Tambah Level' : 'Add Level')
                                    }
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nama (ID)</Label>
                                        <Input
                                            value={levelForm.name}
                                            onChange={(e) => setLevelForm({...levelForm, name: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                            data-testid="level-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Name (EN)</Label>
                                        <Input
                                            value={levelForm.name_en}
                                            onChange={(e) => setLevelForm({...levelForm, name_en: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Deskripsi (ID)</Label>
                                    <Textarea
                                        value={levelForm.description}
                                        onChange={(e) => setLevelForm({...levelForm, description: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Description (EN)</Label>
                                    <Textarea
                                        value={levelForm.description_en}
                                        onChange={(e) => setLevelForm({...levelForm, description_en: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Order</Label>
                                        <Input
                                            type="number"
                                            value={levelForm.order}
                                            onChange={(e) => setLevelForm({...levelForm, order: parseInt(e.target.value)})}
                                            className="bg-slate-700 border-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Icon</Label>
                                        <Input
                                            value={levelForm.icon}
                                            onChange={(e) => setLevelForm({...levelForm, icon: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                        />
                                    </div>
                                </div>
                                <Button onClick={handleSaveLevel} className="w-full" data-testid="save-level-btn">
                                    {language === 'id' ? 'Simpan' : 'Save'}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Levels List */}
                <Accordion type="single" collapsible className="space-y-4">
                    {levels.map((level) => {
                        const levelChapters = chapters.filter(c => c.level_id === level.id);
                        
                        return (
                            <AccordionItem 
                                key={level.id} 
                                value={level.id}
                                className="bg-slate-800 border-slate-700 rounded-lg overflow-hidden"
                            >
                                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-700/50">
                                    <div className="flex items-center gap-4 text-left">
                                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                                            <BookOpen className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-heading font-semibold text-white">{level.name}</h3>
                                            <p className="text-sm text-slate-400">
                                                {levelChapters.length} chapters
                                            </p>
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4">
                                    {/* Level Actions */}
                                    <div className="flex gap-2 mb-4">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                                            onClick={() => {
                                                setEditingLevel(level);
                                                setLevelForm(level);
                                                setLevelDialog(true);
                                            }}
                                        >
                                            <Pencil className="w-4 h-4 mr-1" /> Edit
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="bg-red-900/30 border-red-800 text-red-400 hover:bg-red-900/50"
                                            onClick={() => handleDeleteLevel(level.id)}
                                        >
                                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                                        </Button>
                                        <Dialog open={chapterDialog && selectedLevel === level.id} onOpenChange={(open) => {
                                            setChapterDialog(open);
                                            if (open) setSelectedLevel(level.id);
                                        }}>
                                            <DialogTrigger asChild>
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedLevel(level.id);
                                                        setEditingChapter(null);
                                                        setChapterForm({ title: '', title_en: '', description: '', description_en: '', order: levelChapters.length + 1, level_id: level.id });
                                                    }}
                                                >
                                                    <Plus className="w-4 h-4 mr-1" /> Add Chapter
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="bg-slate-800 border-slate-700 text-white">
                                                <DialogHeader>
                                                    <DialogTitle>
                                                        {editingChapter ? 'Edit Chapter' : 'Add Chapter'}
                                                    </DialogTitle>
                                                </DialogHeader>
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-2">
                                                            <Label>Judul (ID)</Label>
                                                            <Input
                                                                value={chapterForm.title}
                                                                onChange={(e) => setChapterForm({...chapterForm, title: e.target.value})}
                                                                className="bg-slate-700 border-slate-600"
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Title (EN)</Label>
                                                            <Input
                                                                value={chapterForm.title_en}
                                                                onChange={(e) => setChapterForm({...chapterForm, title_en: e.target.value})}
                                                                className="bg-slate-700 border-slate-600"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Order</Label>
                                                        <Input
                                                            type="number"
                                                            value={chapterForm.order}
                                                            onChange={(e) => setChapterForm({...chapterForm, order: parseInt(e.target.value)})}
                                                            className="bg-slate-700 border-slate-600"
                                                        />
                                                    </div>
                                                    <Button onClick={handleSaveChapter} className="w-full">
                                                        {language === 'id' ? 'Simpan' : 'Save'}
                                                    </Button>
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    </div>

                                    {/* Chapters */}
                                    <div className="space-y-3">
                                        {levelChapters.map((chapter) => {
                                            const chapterLessons = lessons.filter(l => l.chapter_id === chapter.id);
                                            
                                            return (
                                                <div key={chapter.id} className="bg-slate-700/50 rounded-lg p-4">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-6 h-6 rounded bg-slate-600 flex items-center justify-center text-xs text-white">
                                                                {chapter.order}
                                                            </span>
                                                            <span className="font-medium text-white">{chapter.title}</span>
                                                            <span className="text-xs text-slate-400">({chapterLessons.length} lessons)</span>
                                                        </div>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-slate-400 hover:text-white"
                                                                onClick={() => {
                                                                    setEditingChapter(chapter);
                                                                    setChapterForm({...chapter});
                                                                    setSelectedLevel(level.id);
                                                                    setChapterDialog(true);
                                                                }}
                                                            >
                                                                <Pencil className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-red-400 hover:text-red-300"
                                                                onClick={() => handleDeleteChapter(chapter.id)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                            <Dialog open={lessonDialog && selectedChapter === chapter.id} onOpenChange={(open) => {
                                                                setLessonDialog(open);
                                                                if (open) setSelectedChapter(chapter.id);
                                                            }}>
                                                                <DialogTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="text-primary hover:text-primary/80"
                                                                        onClick={() => {
                                                                            setSelectedChapter(chapter.id);
                                                                            setEditingLesson(null);
                                                                            setLessonForm({ 
                                                                                title: '', title_en: '', description: '', description_en: '', 
                                                                                video_url: '', duration_minutes: 10, 
                                                                                order: chapterLessons.length + 1, 
                                                                                chapter_id: chapter.id, 
                                                                                is_locked: true 
                                                                            });
                                                                        }}
                                                                    >
                                                                        <Plus className="w-4 h-4" />
                                                                    </Button>
                                                                </DialogTrigger>
                                                                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
                                                                    <DialogHeader>
                                                                        <DialogTitle>
                                                                            {editingLesson ? 'Edit Lesson' : 'Add Lesson'}
                                                                        </DialogTitle>
                                                                    </DialogHeader>
                                                                    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                                                                        <div className="grid grid-cols-2 gap-4">
                                                                            <div className="space-y-2">
                                                                                <Label>Judul (ID)</Label>
                                                                                <Input
                                                                                    value={lessonForm.title}
                                                                                    onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})}
                                                                                    className="bg-slate-700 border-slate-600"
                                                                                />
                                                                            </div>
                                                                            <div className="space-y-2">
                                                                                <Label>Title (EN)</Label>
                                                                                <Input
                                                                                    value={lessonForm.title_en}
                                                                                    onChange={(e) => setLessonForm({...lessonForm, title_en: e.target.value})}
                                                                                    className="bg-slate-700 border-slate-600"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            <Label>Deskripsi (ID)</Label>
                                                                            <Textarea
                                                                                value={lessonForm.description}
                                                                                onChange={(e) => setLessonForm({...lessonForm, description: e.target.value})}
                                                                                className="bg-slate-700 border-slate-600"
                                                                            />
                                                                        </div>
                                                                        
                                                                        {/* Video Upload Section */}
                                                                        <div className="space-y-3">
                                                                            <Label className="flex items-center gap-2">
                                                                                <Video className="w-4 h-4" />
                                                                                Video Lesson
                                                                            </Label>
                                                                            
                                                                            {lessonForm.video_url ? (
                                                                                <div className="space-y-2">
                                                                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                                                                        {isYoutubeUrl(lessonForm.video_url) ? (
                                                                                            <Youtube className="w-5 h-5 text-red-500" />
                                                                                        ) : (
                                                                                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                                                                                        )}
                                                                                        <span className="text-sm text-emerald-400 flex-1 truncate">
                                                                                            {lessonForm.video_url}
                                                                                        </span>
                                                                                        <Button 
                                                                                            type="button" 
                                                                                            size="icon" 
                                                                                            variant="ghost"
                                                                                            onClick={clearVideoUrl}
                                                                                            className="h-8 w-8 text-slate-400 hover:text-red-400"
                                                                                        >
                                                                                            <X className="w-4 h-4" />
                                                                                        </Button>
                                                                                    </div>
                                                                                    {/* Video Preview */}
                                                                                    {isYoutubeUrl(lessonForm.video_url) ? (
                                                                                        <iframe
                                                                                            src={lessonForm.video_url}
                                                                                            className="w-full aspect-video rounded-lg"
                                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                                            allowFullScreen
                                                                                            title="YouTube Preview"
                                                                                        />
                                                                                    ) : (
                                                                                        <video 
                                                                                            src={lessonForm.video_url}
                                                                                            controls
                                                                                            className="w-full rounded-lg max-h-40"
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <Tabs value={videoSourceTab} onValueChange={setVideoSourceTab} className="w-full">
                                                                                    <TabsList className="grid w-full grid-cols-2 bg-slate-700">
                                                                                        <TabsTrigger value="youtube" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
                                                                                            <Youtube className="w-4 h-4 mr-2" />
                                                                                            YouTube
                                                                                        </TabsTrigger>
                                                                                        <TabsTrigger value="upload" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                                                                                            <Upload className="w-4 h-4 mr-2" />
                                                                                            Upload
                                                                                        </TabsTrigger>
                                                                                    </TabsList>
                                                                                    
                                                                                    <TabsContent value="youtube" className="mt-3">
                                                                                        <div className="space-y-3">
                                                                                            <div className="flex gap-2">
                                                                                                <Input
                                                                                                    value={youtubeUrl}
                                                                                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                                                                                    placeholder="youtube.com/watch?v=... atau /shorts/..."
                                                                                                    className="bg-slate-700 border-slate-600 flex-1"
                                                                                                />
                                                                                                <Button 
                                                                                                    type="button"
                                                                                                    onClick={handleYoutubeUrl}
                                                                                                    disabled={!youtubeUrl}
                                                                                                    className="bg-red-500 hover:bg-red-600"
                                                                                                >
                                                                                                    <LinkIcon className="w-4 h-4 mr-2" />
                                                                                                    {language === 'id' ? 'Tambah' : 'Add'}
                                                                                                </Button>
                                                                                            </div>
                                                                                            <p className="text-xs text-slate-400">
                                                                                                {language === 'id' 
                                                                                                    ? 'Paste URL YouTube atau Shorts. Video akan dimainkan terus dalam portal.'
                                                                                                    : 'Paste YouTube or Shorts URL. Video will play directly in portal.'
                                                                                                }
                                                                                            </p>
                                                                                        </div>
                                                                                    </TabsContent>
                                                                                    
                                                                                    <TabsContent value="upload" className="mt-3">
                                                                                        <div className="space-y-2">
                                                                                            <input
                                                                                                ref={videoInputRef}
                                                                                                type="file"
                                                                                                accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo"
                                                                                                onChange={handleVideoUpload}
                                                                                                className="hidden"
                                                                                                id="video-upload"
                                                                                                disabled={uploading}
                                                                                            />
                                                                                            <label 
                                                                                                htmlFor="video-upload"
                                                                                                className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                                                                                                    uploading 
                                                                                                        ? 'border-primary/50 bg-primary/5' 
                                                                                                        : 'border-slate-600 hover:border-primary/50 hover:bg-slate-700/50'
                                                                                                }`}
                                                                                            >
                                                                                                {uploading ? (
                                                                                                    <>
                                                                                                        <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
                                                                                                        <span className="text-sm text-slate-300">
                                                                                                            {language === 'id' ? 'Memuat naik...' : 'Uploading...'} {uploadProgress}%
                                                                                                        </span>
                                                                                                        <Progress value={uploadProgress} className="w-full mt-2 h-2" />
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                                                                                        <span className="text-sm text-slate-300 text-center">
                                                                                                            {language === 'id' 
                                                                                                                ? 'Klik untuk muat naik video'
                                                                                                                : 'Click to upload video'
                                                                                                            }
                                                                                                        </span>
                                                                                                        <span className="text-xs text-slate-500 mt-1">
                                                                                                            MP4, WebM, OGG, MOV, AVI (Max 500MB)
                                                                                                        </span>
                                                                                                    </>
                                                                                                )}
                                                                                            </label>
                                                                                        </div>
                                                                                    </TabsContent>
                                                                                </Tabs>
                                                                            )}
                                                                        </div>
                                                                        <div className="grid grid-cols-2 gap-4">
                                                                            <div className="space-y-2">
                                                                                <Label>Durasi (menit)</Label>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={lessonForm.duration_minutes}
                                                                                    onChange={(e) => setLessonForm({...lessonForm, duration_minutes: parseInt(e.target.value)})}
                                                                                    className="bg-slate-700 border-slate-600"
                                                                                />
                                                                            </div>
                                                                            <div className="space-y-2">
                                                                                <Label>Order</Label>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={lessonForm.order}
                                                                                    onChange={(e) => setLessonForm({...lessonForm, order: parseInt(e.target.value)})}
                                                                                    className="bg-slate-700 border-slate-600"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex items-center justify-between">
                                                                            <Label>Locked</Label>
                                                                            <Switch
                                                                                checked={lessonForm.is_locked}
                                                                                onCheckedChange={(checked) => setLessonForm({...lessonForm, is_locked: checked})}
                                                                            />
                                                                        </div>
                                                                        <Button onClick={handleSaveLesson} className="w-full">
                                                                            {language === 'id' ? 'Simpan' : 'Save'}
                                                                        </Button>
                                                                    </div>
                                                                </DialogContent>
                                                            </Dialog>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Lessons */}
                                                    <div className="space-y-2 ml-8">
                                                        {chapterLessons.map((lesson) => (
                                                            <div 
                                                                key={lesson.id}
                                                                className="flex items-center justify-between p-2 rounded bg-slate-600/50"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {lesson.is_locked ? (
                                                                        <Lock className="w-4 h-4 text-slate-400" />
                                                                    ) : (
                                                                        <Unlock className="w-4 h-4 text-emerald-400" />
                                                                    )}
                                                                    <span className="text-sm text-slate-300">{lesson.title}</span>
                                                                    <span className="text-xs text-slate-500">({lesson.duration_minutes}m)</span>
                                                                </div>
                                                                <div className="flex gap-1">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                                                                        onClick={() => toggleLessonLock(lesson)}
                                                                    >
                                                                        {lesson.is_locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                                                                        onClick={() => {
                                                                            setEditingLesson(lesson);
                                                                            setLessonForm({...lesson});
                                                                            setSelectedChapter(chapter.id);
                                                                            setLessonDialog(true);
                                                                        }}
                                                                    >
                                                                        <Pencil className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                                                                        onClick={() => handleDeleteLesson(lesson.id)}
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>
        </AdminLayout>
    );
};
